// frontend/src/routes.js
// Centralised path ↔ tabId mapping used by the sidebar, breadcrumbs,
// and the useRouteSync hook that bridges React-Router URLs to the
// legacy activeTab state.

/**
 * Each entry: { path, tabId, label, parent? }
 *   path    – React-Router path pattern (relative to "/")
 *   tabId   – the legacy activeTab value so existing components keep working
 *   label   – human-readable breadcrumb / page title
 *   parent  – optional tabId of the logical parent (for breadcrumbs)
 */
const routes = [
  // ── Main ──────────────────────────────────────────────────
  { path: '/',                          tabId: 'dashboard',                   label: 'Dashboard' },

  // ── Practice Management ───────────────────────────────────
  { path: '/clients',                   tabId: 'clients',                     label: 'Clients' },
  { path: '/cases',                     tabId: 'cases',                       label: 'Cases' },
  { path: '/cases/:caseId',             tabId: 'caseDetails',                 label: 'Case Details',        parent: 'cases' },
  { path: '/cases/:caseId/:subtab',     tabId: 'caseDetails',                 label: 'Case Details',        parent: 'cases' },
  { path: '/documents',                 tabId: 'documents',                   label: 'Documents' },
  { path: '/documents/editor',          tabId: 'documentEditor',              label: 'Document Editor',     parent: 'documents' },
  { path: '/documents/editor/:docId',   tabId: 'documentEditor',              label: 'Document Editor',     parent: 'documents' },
  { path: '/tasks',                     tabId: 'tasks',                       label: 'Tasks' },
  { path: '/task-templates',            tabId: 'taskTemplates',               label: 'Task Templates' },
  { path: '/calendar',                  tabId: 'calendar',                    label: 'Calendar' },

  // ── Time & Billing ────────────────────────────────────────
  { path: '/time-expense',              tabId: 'timeExpense',                 label: 'Time & Disbursements' },
  { path: '/services',                  tabId: 'serviceProducts',             label: 'Services' },
  { path: '/billing',                   tabId: 'billing',                     label: 'Billing' },
  { path: '/debt-recovery',             tabId: 'debtCollectionDashboard',     label: 'Debt Recovery' },
  { path: '/collections-settings',      tabId: 'collectionsSettings',         label: 'Collections Settings' },
  { path: '/bills-of-costs',            tabId: 'billsOfCosts',               label: 'Bills of Costs' },

  // ── Trust & Accounting ────────────────────────────────────
  { path: '/trust-accounting',              tabId: 'trustAccounting',         label: 'Trust Accounting' },
  { path: '/accounting/chart-of-accounts',  tabId: 'chartOfAccounts',         label: 'Chart of Accounts' },
  { path: '/accounting/general-ledger',     tabId: 'generalLedger',           label: 'General Ledger' },
  { path: '/accounting/suppliers',          tabId: 'suppliers',               label: 'Suppliers' },
  { path: '/accounting/creditors',          tabId: 'creditors',               label: 'Creditors' },
  { path: '/accounting/ap-bills',           tabId: 'apBills',                 label: 'AP Bills' },
  { path: '/accounting/petty-cash',         tabId: 'pettyCash',               label: 'Petty Cash' },
  { path: '/accounting/review',             tabId: 'accountingReview',        label: 'Accounting Review' },
  { path: '/accounting/firm-expenses',      tabId: 'firmExpenses',            label: 'Firm Expenses' },
  { path: '/accounting/bank-accounts',      tabId: 'bankAccounts',            label: 'Bank Accounts' },
  { path: '/accounting/bank-reconciliation',tabId: 'bankReconciliation',      label: 'Bank Reconciliation' },
  { path: '/accounting/vat-returns',        tabId: 'vatReturns',              label: 'VAT Returns' },
  { path: '/accounting/trial-balance',      tabId: 'trialBalance',            label: 'Trial Balance' },
  { path: '/accounting/balance-sheet',      tabId: 'balanceSheet',            label: 'Balance Sheet' },
  { path: '/accounting/income-statement',   tabId: 'incomeStatement',         label: 'Income Statement' },
  { path: '/accounting/cash-flow',          tabId: 'cashFlow',                label: 'Cash Flow' },
  { path: '/accounting/periods',            tabId: 'accountingPeriods',       label: 'Accounting Periods' },

  // ── Reports & Logs ────────────────────────────────────────
  { path: '/reports',                   tabId: 'reports',                     label: 'Reports' },
  { path: '/communication-logs',        tabId: 'communicationLogs',           label: 'Logs' },

  // ── People & Performance ──────────────────────────────────
  { path: '/activity-log',             tabId: 'activityLog',                  label: 'Activity Log' },
  { path: '/my-performance',           tabId: 'myPerformance',                label: 'My Performance' },
  { path: '/payroll',                  tabId: 'payrollManagement',            label: 'Payroll & HR' },
  { path: '/team-overview',            tabId: 'teamOverview',                 label: 'Team Overview' },
  { path: '/performance-reviews',      tabId: 'performanceReviews',           label: 'Reviews' },
  { path: '/departments',             tabId: 'departments',                   label: 'Departments' },

  // ── Compliance & Audit ────────────────────────────────────
  { path: '/compliance',              tabId: 'complianceHub',                 label: 'Compliance Hub' },
  { path: '/attorney-records',        tabId: 'attorneyRecords',               label: 'Attorney Records' },
  { path: '/audit',                   tabId: 'auditManagement',               label: 'Audit Management' },
  { path: '/manage-auditors',         tabId: 'manageAuditors',                label: 'Manage Auditors' },
  { path: '/annual-certificates',     tabId: 'annualCertificateManagement',   label: 'Annual Certificates' },

  // ── Conveyancing ─────────────────────────────────────────
  { path: '/conveyancing',                    tabId: 'conveyancingDashboard',  label: 'Conv. Dashboard' },
  { path: '/conveyancing/properties',         tabId: 'properties',             label: 'Property Register',   parent: 'conveyancingDashboard' },
  { path: '/conveyancing/bank-inbox',         tabId: 'bankInstructionInbox',   label: 'Bank Inbox',          parent: 'conveyancingDashboard' },
  { path: '/conveyancing/batches',            tabId: 'conveyancingBatches',    label: 'Batch Lodgements',    parent: 'conveyancingDashboard' },
  { path: '/conveyancing/reports',            tabId: 'conveyancingReports',    label: 'Conv. Reports',       parent: 'conveyancingDashboard' },

  // ── Practice extras ───────────────────────────────────────
  { path: '/clause-library',          tabId: 'clauseLibrary',                  label: 'Clause Library' },

  // ── Administration ────────────────────────────────────────
  { path: '/imports',                 tabId: 'imports',                        label: 'Imports' },
  { path: '/settings/firm',          tabId: 'firmSettings',                    label: 'Firm Settings' },
  { path: '/settings/integrations',  tabId: 'integrations',                    label: 'Integrations' },
  { path: '/settings/security',     tabId: 'security',                         label: 'Security (2FA)' },
  { path: '/inbox',                 tabId: 'unlinkedInbox',                    label: 'Outlook Inbox' },
  { path: '/email-rules',           tabId: 'emailRules',                       label: 'Email Rules' },
];

// ── Lookup helpers ──────────────────────────────────────────

/** tabId → first matching path (for navigation) */
const tabToPath = Object.fromEntries(
  // Reverse so that more-specific routes don't shadow the first simple one.
  // We want tabId → simplest path (no params).
  routes
    .filter(r => !r.path.includes(':'))
    .map(r => [r.tabId, r.path])
);

/** path (no params) → route entry */
const pathToRoute = Object.fromEntries(
  routes.filter(r => !r.path.includes(':'))
    .map(r => [r.path, r])
);

/**
 * Given a tabId, return the URL path to navigate to.
 * Falls back to '/' if the tabId is unknown.
 */
export function pathForTab(tabId) {
  return tabToPath[tabId] || '/';
}

/**
 * Given a pathname, find the matching route entry.
 * Handles parameterised routes by trying exact match first,
 * then stripping trailing segments until a match is found.
 */
export function routeForPath(pathname) {
  // Exact match (non-parameterised)
  if (pathToRoute[pathname]) return pathToRoute[pathname];

  // Try parameterised routes:
  // /cases/123           → match /cases/:caseId
  // /cases/123/billing   → match /cases/:caseId/:subtab
  // /documents/editor/5  → match /documents/editor/:docId
  for (const r of routes) {
    if (!r.path.includes(':')) continue;
    const patternParts = r.path.split('/');
    const pathParts = pathname.split('/');
    if (patternParts.length !== pathParts.length) continue;
    const matches = patternParts.every((part, i) =>
      part.startsWith(':') || part === pathParts[i]
    );
    if (matches) return r;
  }

  return null;
}

/**
 * Build breadcrumb trail for a given pathname.
 * Returns array of { label, path } objects.
 */
export function breadcrumbsForPath(pathname) {
  const route = routeForPath(pathname);
  if (!route) return [{ label: 'Dashboard', path: '/' }];

  // Pre-build a lookup for non-parameterised routes by tabId
  const byTabId = {};
  for (const r of routes) {
    if (!r.path.includes(':') && !byTabId[r.tabId]) byTabId[r.tabId] = r;
  }

  // Walk the parent chain
  const crumbs = [];
  const visited = new Set();
  let cur = route;
  while (cur && !visited.has(cur.tabId)) {
    visited.add(cur.tabId);
    crumbs.unshift({ label: cur.label, path: cur.path.includes(':') ? pathname : cur.path });
    cur = cur.parent ? (byTabId[cur.parent] || null) : null;
  }
  if (crumbs.length === 0 || crumbs[0].path !== '/') {
    crumbs.unshift({ label: 'Dashboard', path: '/' });
  }
  return crumbs;
}

export default routes;
